/**
 * BottomSheet (자체 구현)
 *
 * mobiscroll 의존을 제거한 vanilla 구현. mobiscroll v5 가 우리 element 에 wrapper
 * className 머지, React-style 재렌더링으로 우리 클래스 덮어쓰기, scrollLock 의 _lock
 * 플래그가 nested scroll 차단 같은 누적된 함정들 때문에 자체 구현으로 전환.
 *
 * DOM:
 *   <div class="bottomsheet-popup [is-open] [bs-fixed-height]">
 *     <div class="bottomsheet-overlay"></div>      <!-- dim, 클릭 무시 -->
 *     <div class="bottomsheet-panel">
 *       <div class="header">
 *         <button class="bs-prev|bs-next|bs-close">…</button>
 *         <h2>…</h2>
 *       </div>
 *       <div class="content-area" id="bs-xxx"></div>
 *     </div>
 *   </div>
 *
 * API (기존 호환):
 *   $j.Ui.BS.open({ target, data, fixedHeight, headerBg, onPrev, onNext, backClose,
 *                   title, subtitle, step, action })   — 아래 넷은 슬롯 헤더용(아래 슬롯 헤더)
 *   $j.Ui.BS.close()
 *   $j.Ui.BS.setTitle(title)
 *   $j.Ui.BS.setHeader({ title, meta, subtitle, loading })
 *   $j.Ui.BS.setBand(content)   — 탭·검색처럼 스크롤돼도 남아야 하는 것을 헤더 아래 띠로 옮긴다
 *   $j.Ui.BS.collapseTitle(el)  — 본문의 큰 제목이 스크롤로 가려지면 헤더 제목을 그 글자로 바꾼다(글 읽기 시트)
 *   $j.Ui.BS.enableBackClose()  — 열린 시트에 「뒤로가기로 닫기」를 나중에 켠다(아래 뒤로가기 닫기)
 *   $j.Ui.BS.init({ dragBar, dimOpacity, looseDrag, fullScreen, stopAnywhere, header })  — 사이트 부트스트랩에서 1회(아래 init 주석)
 *   target = { init(), destroy?() } — this.id / this.parent / this.data 주입
 *
 * 뒤로가기 닫기 (backClose:true 를 준 호출부만):
 *   열 때 히스토리 항목을 하나 얹고 popstate 로 닫는다. 닫기 버튼으로 닫을 때는 그 항목을
 *   history.back() 으로 회수한다 — 안 하면 시트를 닫은 뒤 뒤로가기 한 번이 먹통이 된다.
 *   기본값 off 라 옵션을 안 준 화면(/m·/b)의 동작은 종전과 같다.
 *   열 때는 정할 수 없고 안의 내용이 정해진 뒤에야 켤지 아는 호출부는 enableBackClose() 로 나중에 켠다
 *   (일지 사진 격자 — 사진 전체 허용일 때만, 2026-10-27).
 *
 * 슬롯 헤더 (init header:'slot' 을 켠 사이트만 — 끈 사이트는 위 DOM 그대로):
 *   [← 이전] [제목 + 메타 / 부제] [글자 버튼] [› 다음] [✕]  +  아래 띠(진행 막대 · 탭·검색)
 *   헤더를 본문 위에 겹쳐 띄우지 않고 쌓으며, 치수는 글자크기와 무관하게 고정한다 (_bs.scss 「슬롯 헤더」).
 *   비어 있는 칸은 자리를 차지하지 않는다. 기존 호출부(setTitle·onPrev/onNext)는 고치지 않아도 그대로 돈다.
 *
 * Body lock:
 *   open 시 window.scrollY 저장 → body { position:fixed; top: -scrollY; width: 100% }
 *   close 시 복원 + window.scrollTo(0, scrollY). iOS 까지 안전한 표준 패턴.
 */
const BS_DURATION = 250; // CSS transition 과 동기

// 슬롯 헤더 아이콘 — 인라인 SVG (현행 헤더는 Font Awesome 그대로)
const BS_SVG = (d) => `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${d}</svg>`;
const BS_IC = {
    back:  BS_SVG('<path d="M19 12H5M12 19l-7-7 7-7"/>'),
    prev:  BS_SVG('<path d="M15 6l-6 6 6 6"/>'),
    next:  BS_SVG('<path d="M9 6l6 6-6 6"/>'),
    close: BS_SVG('<path d="M6 6l12 12M18 6L6 18"/>'),
};

export default class BS {
    constructor() {
        this.divId = null;
        this.target = null;
        this.bsClsName = 'bottomsheet-popup';
        this.fixedHeight = false;
        this._savedScrollY = 0;
        this._closeTimer = null;
        this._histOn = false;   // 뒤로가기용 히스토리 항목을 얹어둔 상태인가
        this._onPop = null;
        this._dragBar = false;  // 헤더 손잡이(회색 바) 표시 여부 — 기본 숨김, init() 으로 켜는 사이트만 노출
        this._dimOpacity = null; // dim 농도 override — 기본 null(= CSS 기본값 .5), init() 으로 사이트별 조정
        this._looseDrag = false; // 드래그 닫기 판정 완화(60px/속도기준)+본문 드래그 허용 — 기본 false(기존 동작)
        this._fullScreen = false; // 시트가 화면 전체를 덮는다(.bs-full) — 기본 false(종전 높이), init() 으로 켜는 사이트만
        this._stopAnywhere = false; // 끌어내리다 손 떼면 그 자리에 멈춘다(스냅백 안 함) — 기본 false(원위치 복귀)
        this._dragRestY = 0;     // stopAnywhere 로 멈춰둔 현재 내림 오프셋(px). open/close 마다 0 으로 초기화
        this._slot = false;      // 슬롯 헤더(header:'slot') — 기본 false(현행 헤더)
        this._titleAlign = 'center'; // 슬롯 헤더 제목 정렬 기본값 — init({titleAlign}) 로 사이트별
        this._hdLoading = false; // setHeader({loading}) 로 제목·부제 자리를 잡아둔 상태
        this._ct = null;         // collapseTitle — { el, base, on }. open 마다 초기화
    }

    /**
     * 전역 옵션 설정 — 사이트 부트스트랩에서 1회 호출.
     * @param option
     *   dragBar: boolean — 헤더 손잡이(가운데 회색 바) 표시 여부. 기본 false(숨김). fullScreen 이면 켜도 그리지 않는다.
     *            드래그로 닫는 제스처 자체는 이 옵션과 무관하게 항상 동작한다(시각 표시만 제어).
     *            (2026-08 홍과장님 요청 — 여러 사이트가 같이 쓰는 공용 컴포넌트라, 악덕알리미만
     *             명시적으로 켜고 나머지(장비불러·모바일관리자 등)는 기본값 그대로 숨긴다)
     *   dimOpacity: number — 오버레이(dim) 농도(0~1). 기본은 CSS 값(.5) 그대로 사용.
     *            (2026-08 기획자 피드백 — 시트 위 살짝 보이는 뒷페이지 제목이 너무 어두워 안 읽힘.
     *             dragBar 와 같은 이유로 사이트별 옵션으로 두고, 공용 기본값은 건드리지 않는다)
     *   looseDrag: boolean — true 면 드래그로 닫는 판정을 완화(거리 60px 또는 속도 0.3px/ms 이상,
     *            기본은 110px 거리만)하고 헤더뿐 아니라 본문(스크롤 맨 위일 때)에서도 드래그
     *            시작을 허용한다. 기본 false — 장비불러·모바일관리자는 시트 안에 직접 입력하는
     *            폼(차량번호·금액·전화번호·회원/오더 편집 등)이 많아, 판정을 완화하면 입력 중
     *            실수로 시트가 닫혀 값이 날아갈 위험이 있다. (2026-08 상필 님 QA로 악덕알리미만
     *            먼저 완화했다가, 이 옵션이 전 사이트 공용 로직이라 장비불러에도 그대로 새던 걸
     *            발견 — dragBar 와 같은 이유로 사이트별 옵션으로 분리)
     *   fullScreen: boolean — true 면 시트가 화면 전체를 덮는다(.bs-full: 위·아래 0, 모서리 둥글림 없음).
     *            open() 의 fixedHeight 높이 계산보다 우선한다. 기본 false(내용만큼, 최대 화면-5vh).
     *   stopAnywhere: boolean — true 면 끌어내리다 손을 뗀 자리에 시트가 "멈춘다"(원위치로 스냅백하지
     *            않음). 다시 잡으면 그 자리에서 이어서 끌 수 있고, 위로 끝까지(맨 위 근처) 끌면 원위치로
     *            되돌아가며, 패널 높이의 40% 이상 내리거나 아래로 휙 튕기면(0.3px/ms 이상) close() 된다.
     *            기본 false(끝까지 안 내리면 원위치 복귀 — 종전 동작). (2026-10 홍과장님 요청 — 장비불러·악덕알리미)
     *   header: 'slot' — 슬롯 헤더(파일 머리 주석)로 그린다. 기본(미지정)은 현행 헤더(가운데 제목 · 겹쳐 뜬 헤더).
     *            DOM 을 처음 열 때 한 번 만들므로 첫 open 전에 정해야 한다.
     *   titleAlign: 'center'|'left' — 슬롯 헤더 제목 정렬 기본값. 기본 'center'(종전 모양 — 고정 제목은 가운데).
     *            open({titleAlign}) 로 시트마다 바꾸고, collapseTitle·action 을 쓰는 시트는 자동으로 왼쪽이다.
     */
    init(option = {}) {
        this._dragBar = option.dragBar === true;
        this._dimOpacity = typeof option.dimOpacity === 'number' ? option.dimOpacity : null;
        this._looseDrag = option.looseDrag === true;
        this._fullScreen = option.fullScreen === true;
        this._stopAnywhere = option.stopAnywhere === true;
        this._slot = option.header === 'slot';
        this._titleAlign = option.titleAlign === 'left' ? 'left' : 'center';
    }

    /**
     * 인스턴스 제거시 처리 (Div / EventBus 정리)
     */
    destroy() {
        $j.Ui.Div.destroyByContainer(this);
        $j.Common.EventBus.offNamespace($j.getNS(this));
    }

    /**
     * 바텀시트를 연다.
     * @param option
     *   target:      문자열 키('$j.F.Diary.DiaryPhotoHub') → $j.reset 으로 새 인스턴스 생성
     *                또는 init() 메서드를 가진 객체 → 그대로 사용 (ad-hoc adapter 패턴)
     *   data:        타겟에 전달할 파라미터
     *   fixedHeight: true 시 panel 을 95vh 로 고정 (.bs-fixed-height 클래스 부여)
     *   headerBg:    'gray' 시 헤더 배경 회색 (.header-gray 클래스 부여)
     *   onPrev:      함수 — 헤더 좌측 prev 버튼 클릭시 호출 (지정시 버튼 노출)
     *   onNext:      함수 — 헤더 우측 next 버튼 클릭시 호출 (지정시 버튼 노출)
     *   backClose:   true 시 안드로이드 뒤로가기로 닫힌다 (기본 false — 히스토리를 건드리지 않음)
     *   title:       제목(HTML). setTitle 과 같다 — target.init() 이 setTitle 하면 그쪽이 이긴다
     *   ── 슬롯 헤더에서만 (현행 헤더는 무시) ──
     *   subtitle:    제목 아래 회색 한 줄
     *   step:        { cur, total } — 헤더 아래 진행 막대 (단계형 시트)
     *   action:      { label, onClick } — 닫기 앞 글자 버튼 1개 (초기화 등)
     *   titleAlign:  'center'|'left' — 이 시트만 제목 정렬을 바꾼다(기본은 init 의 사이트 값)
     *   layout:      'classic' — 슬롯 헤더 안에서 종전 모양으로 그린다(가운데 제목 · 제목 양옆 ‹ › · headerBg 회색).
     *                손잡이·닫기 X·글자크기 고정은 그대로. 발주 단계 시트가 쓴다
     *   onPrev/onNext 는 슬롯 헤더에서 각각 있을 때만 ←(앞칸) · ›(닫기 앞) 로 보인다 (classic 은 종전처럼 둘 다)
     */
    open(option = {}) {
        const self = this;
        let target;
        if (typeof option.target === 'string') {
            target = $j.reset(option.target);
        } else if (option.target && typeof option.target.init === 'function') {
            target = option.target;
        } else {
            throw new Error('BS.open: target must be a string key or an object with init()');
        }

        const data = option.data;
        this.fixedHeight = !!option.fixedHeight;

        // 닫는 도중에 다시 열린 경우 cleanup 타이머 취소 (DOM 재사용)
        if (this._closeTimer) { clearTimeout(this._closeTimer); this._closeTimer = null; }

        // 이미 열려있는 BS 위에 새 target 으로 전환되는 경우 — 이전 target 정리
        if (this.target && this.target !== target && typeof this.target.destroy === 'function') {
            this.target.destroy();
        }
        this.target = target;

        let divId = null;
        let $el = $(`.${this.bsClsName}`);
        if ($el.length > 0) {
            divId = $el.find('.content-area').attr('id');
        } else {
            divId = 'bs-' + Math.random().toString(36).substring(2, 12);
            const header = this._slot ? `
        <div class="header">
            <div class="bs-hd-row">
                <button class="bs-prev" type="button" aria-label="이전" hidden>${BS_IC.back}</button>
                <div class="bs-hd-tt"><h2></h2><p class="bs-hd-sub" hidden></p></div>
                <button class="bs-act" type="button" hidden></button>
                <button class="bs-next" type="button" aria-label="다음" hidden>${BS_IC.next}</button>
                <button class="bs-close" type="button" aria-label="닫기"><span class="bs-x">${BS_IC.close}</span></button>
            </div>
            <div class="bs-hd-band" hidden></div>
        </div>` : `
        <div class="header">
            <button class="bs-prev" type="button" style="display:none"><i class="fas fa-chevron-left"></i></button>
            <h2></h2>
            <button class="bs-next" type="button" style="display:none"><i class="fas fa-chevron-right"></i></button>
            <button class="bs-close" type="button"><i class="fas fa-times"></i></button>
        </div>`;
            $el = $(`
<div class="${this.bsClsName}${this._slot ? ' bs-slot' : ''}">
    <div class="bottomsheet-overlay"></div>
    <div class="bottomsheet-panel">${header}
        <div class="content-area" id="${divId}"></div>
    </div>
</div>`);
            $('body').append($el);

            // 닫기 버튼
            $el.on('click', '.bs-close', function () {
                self.close();
            });

            // overlay 클릭은 무시 (현재 동작 유지). 필요시 옵션화 가능.
            $el.on('click', '.bottomsheet-overlay', function (e) {
                e.stopPropagation();
            });

            // content-area 스크롤시 헤더에 라인(슬롯 헤더는 그림자) 추가.
            // 본문에 직접 건다 — scroll 은 버블링하지 않아 $el 위임으로는 한 번도 불리지 않았다.
            const $header = $el.find('> .bottomsheet-panel > .header');
            $el.find('> .bottomsheet-panel > .content-area').on('scroll', function () {
                self._syncCollapse();
                const scrollTop = $(this).scrollTop();
                if (scrollTop > 5) {
                    $header.addClass('is-scrolled');
                } else {
                    $header.removeClass('is-scrolled');
                }
            });

            // 헤더를 아래로 끌어 닫기 (drag-to-dismiss).
            //   본문(content-area)이 아니라 헤더에서만 시작한다 — 본문 스크롤과 충돌 방지.
            //   내려간 거리가 임계값(DISMISS_PX) 이상이면 close(), 미만이면 원위치로 되돌린다.
            this._bindDragToClose($el);
        }

        this.divId = divId;

        // 헤더 옵션 — 매 open 마다 재설정 (DOM 재사용 케이스 대비)
        this._ct = null;
        const $header = $el.find('> .bottomsheet-panel > .header');
        $header.removeClass('header-gray has-nav has-prev is-scrolled is-collapsed bs-classic');
        $header.find('h2').html(option.title != null ? option.title : '');
        if (option.headerBg === 'gray') $header.addClass('header-gray');

        const $prev = $header.find('.bs-prev').off('click');
        const $next = $header.find('.bs-next').off('click');
        const hasNav = (typeof option.onPrev === 'function') || (typeof option.onNext === 'function');
        if (this._slot) {
            this._resetSlot($header, $prev, $next, option);
        } else {
            $prev.hide();
            $next.hide();
            if (hasNav) {
                // 네비 모드: 양쪽 버튼 항상 노출, 핸들러 없으면 클릭해도 무반응
                $prev.show();
                $next.show();
                $header.addClass('has-nav');
                if (typeof option.onPrev === 'function') $prev.on('click', option.onPrev);
                if (typeof option.onNext === 'function') $next.on('click', option.onNext);
            }
        }

        // fixedHeight class
        $el.toggleClass('bs-fixed-height', this.fixedHeight);
        // 헤더 손잡이(회색 바) — init() 으로 사이트별 설정된 값을 매 open 마다 재적용(DOM 재사용 대비).
        // 화면 전체를 덮는 시트(fullScreen)는 '끌어 내릴 수 있는 낱장'으로 보이지 않으니 손잡이를 그리지 않는다
        // (끌어 닫기 제스처 자체는 그대로 동작한다).
        $el.toggleClass('bs-no-dragbar', !this._dragBar || this._fullScreen);
        // 화면 전체 — init({fullScreen}) 사이트 옵션. 매 open 마다 재적용(DOM 재사용 대비)
        $el.toggleClass('bs-full', this._fullScreen);
        // dim 농도 — CSS 변수로 오버레이에 전달(미설정이면 지워서 CSS 기본값 .5 로 폴백)
        $el.css('--bs-dim', this._dimOpacity != null ? this._dimOpacity : '');

        // stopAnywhere 로 멈춰둔 내림 오프셋 초기화 — 매 open 은 항상 전체 열림부터 시작한다.
        // (DOM 재사용 시 직전 open 에서 중간에 멈춰둔 인라인 transform 이 남아있을 수 있어 지운다)
        this._dragRestY = 0;
        $el.find('> .bottomsheet-panel').css('transform', '');

        // content-area 초기화 (DOM 을 재사용하므로 이전 target 의 내용이 남는다)
        //
        // ⚠ 비우지 않으면 target 의 init() 구현에 따라 이전 화면이 그대로 남는다.
        //   html() 로 갈아끼우는 target 은 우연히 괜찮지만, 살아있는 DOM 노드를 옮겨오는 target 은
        //   append() 밖에 쓸 수 없어(원본 노드를 파괴하면 안 되므로) 앞 내용 아래에 덧붙는다.
        //   실제로 '캐시내역' → 'MY 메모' 순서로 열면 캐시내역 표가 메모 위에 남았다.
        //
        // 순서 주의: 위에서 이전 target 의 destroy() 를 이미 호출했다. 옮겨온 노드를 제자리로
        // 돌려놓는 것이 destroy() 의 역할이므로, 그 뒤에 비워야 원본을 날리지 않는다.
        $el.find('.content-area').empty().scrollTop(0);

        // 처음 열기일 때만 body lock + 애니메이션
        const wasOpen = $el.hasClass('is-open');
        if (!wasOpen) {
            this._lockBody();
            // transform 슬라이드업 애니메이션 트리거 — DOM append 와 같은 frame 에 class 추가하면
            // 애니메이션이 적용되지 않으므로 RAF 로 다음 frame 에 적용
            requestAnimationFrame(() => {
                $el.addClass('is-open');
            });
        }

        // 타겟에게 제어권을 넘긴다
        target.id = this.divId;
        target.parent = this;
        target.data = data;
        target.init();

        // 뒤로가기 닫기 — 이미 얹어둔 상태면 다시 얹지 않는다
        // (시트 위에서 target 만 갈아끼우는 전환도 '한 겹'이라 뒤로가기 한 번으로 닫혀야 한다)
        if (option.backClose) this._pushHistory();

        this._debug('after-open');
        // AJAX 컨텐츠 안정화 후 한번 더 (디버그용)
        setTimeout(() => this._debug('after-1s'), 1000);
    }

    /**
     * 슬롯 헤더를 이번 open 의 옵션으로 다시 채운다 (DOM 재사용 — 앞 시트의 부제·띠·버튼이 남지 않게).
     * ← · › 는 핸들러가 있는 쪽만 보인다(현행 헤더는 하나만 있어도 둘 다 띄웠다).
     */
    _resetSlot($header, $prev, $next, option) {
        this._hdLoading = false;
        const classic = option.layout === 'classic';
        const onPrev = typeof option.onPrev === 'function' ? option.onPrev : null;
        const onNext = typeof option.onNext === 'function' ? option.onNext : null;
        // classic: 종전처럼 한쪽만 있어도 양쪽을 띄운다(핸들러 없는 쪽은 눌러도 무반응) — 제목이 가운데에 머문다
        const showNav = classic && (onPrev || onNext);
        $header.toggleClass('bs-classic', classic);
        $prev.html(classic ? BS_IC.prev : BS_IC.back);
        $prev.prop('hidden', !(onPrev || showNav));
        $next.prop('hidden', !(onNext || showNav));
        if (onPrev) $prev.on('click', onPrev);
        if (onNext) $next.on('click', onNext);
        $header.toggleClass('has-prev', !!onPrev && !classic);
        // 제목 정렬 — 고정 제목은 가운데(종전), 글 읽기·액션이 붙는 시트는 왼쪽. classic 은 자기 규칙(가운데)을 쓴다
        const hasAction = !!(option.action && option.action.label);
        const align = option.titleAlign || (hasAction ? 'left' : this._titleAlign);
        $header.toggleClass('is-center', !classic && align === 'center');

        this._setSub($header, option.subtitle);

        const $act = $header.find('.bs-act').off('click').empty().prop('hidden', true);
        if (option.action && option.action.label) {
            $act.text(option.action.label).prop('hidden', false);
            if (typeof option.action.onClick === 'function') $act.on('click', option.action.onClick);
        }

        const step = option.step;
        if (step && step.total > 0) {
            const cur = Math.min(Math.max(step.cur, 1), step.total);
            this._setBandHtml($header, `
<div class="bs-step" role="progressbar" aria-valuemin="1" aria-valuemax="${step.total}" aria-valuenow="${cur}">
    <div class="bs-step-bar"><i style="width:${(cur / step.total) * 100}%"></i></div>
    <span class="bs-step-tx">${cur}/${step.total}</span>
</div>`);
        } else {
            this._setBandHtml($header, null);
        }
    }

    _$header() {
        return $(`.${this.bsClsName} > .bottomsheet-panel > .header`);
    }

    _setSub($header, sub) {
        const $sub = $header.find('.bs-hd-sub');
        const has = sub != null && sub !== '';
        $sub.html(has ? sub : '').prop('hidden', !has);
    }

    _setBandHtml($header, content) {
        const $band = $header.find('.bs-hd-band').empty();
        const has = content != null && content !== '';
        if (has) $band.append(content);
        $band.prop('hidden', !has);
    }

    /**
     * 헤더 칸을 고친다. 넘긴 칸만 바뀐다.
     *   title    제목(HTML)
     *   meta     제목 옆에 붙는 뱃지·번호(HTML) — title 과 같이 넘긴다
     *   subtitle 제목 아래 회색 한 줄 (슬롯 헤더만)
     *   loading  true 면 제목·부제 자리를 회색 막대로 잡아둔다 — 다 불러오면 title 로 다시 부른다.
     *            제목에 「불러오는 중…」을 넣었다 갈아끼우는 것보다 덜 흔들린다.
     * 현행 헤더에서는 title·meta 만 h2 에 들어간다.
     */
    setHeader(h = {}) {
        const $header = this._$header();
        if (!$header.length) return;
        const $h2 = $header.find('h2');
        if (h.loading) {
            this._hdLoading = true;
            $h2.html(this._slot ? '<span class="bs-skel"></span>' : '');
            if (this._slot) this._setSub($header, '<span class="bs-skel is-sub"></span>');
            return;
        }
        if (h.title !== undefined) {
            $h2.html((h.title ?? '') + (h.meta ? `<span class="bs-hd-meta">${h.meta}</span>` : ''));
        }
        if (this._slot && (h.subtitle !== undefined || this._hdLoading)) this._setSub($header, h.subtitle);
        this._hdLoading = false;
    }

    /**
     * 헤더 아래 띠 — 탭·검색처럼 본문이 스크롤돼도 남아야 하는 것을 옮겨 단다 (슬롯 헤더만).
     * content 는 jQuery 요소·DOM·HTML. 살아 있는 노드를 넘기면 옮겨지므로 이벤트도 그대로 산다.
     * null 이면 띠를 걷는다. 다음 open 때 띠는 비워진다.
     * @returns {boolean} 띠에 달았는가 — false(현행 헤더)면 호출부가 본문에 그대로 둔다
     */
    setBand(content) {
        if (!this._slot) return false;
        const $header = this._$header();
        if (!$header.length) return false;
        this._setBandHtml($header, content);
        return true;
    }

    /**
     * 접히는 제목 — 본문의 큰 제목(el)이 스크롤로 헤더 뒤로 가려지면 헤더 제목을 그 글자로 바꾸고,
     * 다시 보이면 부를 때의 헤더 제목으로 돌린다. 공지·약관처럼 본문에 큰 제목이 따로 있는 글 읽기 시트용 —
     * 입력 시트에 쓰면 제목이 흔들려 보인다. 다음 open 때 풀린다.
     * 헤더 제목은 setHeader 로 먼저 정해둔 뒤에 부른다(그 값이 '돌아갈 제목'이다).
     */
    collapseTitle(el) {
        const node = el && (el.jquery ? el[0] : el);
        const $header = this._$header();
        if (!node || !$header.length) return;
        this._ct = { el: node, base: $header.find('h2').html(), on: false };
        $header.removeClass('is-center');   // 긴 글 제목이 올라오므로 왼쪽 정렬로
        this._syncCollapse();
    }

    _syncCollapse() {
        const ct = this._ct;
        if (!ct) return;
        const $header = this._$header();
        if (!$header.length || !document.body.contains(ct.el)) { this._ct = null; return; }
        const gone = ct.el.getBoundingClientRect().bottom <= $header[0].getBoundingClientRect().bottom;
        if (gone === ct.on) return;
        ct.on = gone;
        const $h2 = $header.find('h2');
        if (gone) $h2.text(ct.el.textContent.trim());
        else $h2.html(ct.base);
        $header.toggleClass('is-collapsed', gone);
    }

    /** 뒤로가기 몫의 히스토리 항목을 얹고 popstate 를 듣는다 */
    _pushHistory() {
        if (this._histOn) return;
        this._histOn = true;
        history.pushState({ bs: 1 }, '', location.href);
        // popstate 는 등록된 리스너 **전부**에 간다. 시트 위에 겹친 레이어(모달 등)가 자기 항목을
        // 되감을 때도 이 핸들러가 불린다 — 그때 그냥 닫으면 "모달만 닫았는데 시트까지 닫힌다".
        // 되감은 뒤 현재 상태가 아직 내 항목이면(= 위 레이어가 빠진 것) 나는 그대로 있는다.
        this._onPop = () => {
            if (history.state && history.state.bs) return;
            this.close(true);
        };
        window.addEventListener('popstate', this._onPop);
    }

    /**
     * 열려 있는 시트에 「뒤로가기로 닫기」를 나중에 켠다 — open({ backClose:true }) 와 같은 동작.
     * 열려 있지 않거나 닫히는 중이면 아무것도 안 한다. 이미 켜져 있으면 다시 얹지 않는다(_pushHistory).
     */
    enableBackClose() {
        if (!this.target || this._closeTimer) return;
        this._pushHistory();
    }

    /** popstate 리스너 해제 (닫기 경로 공통) */
    _offPop() {
        if (!this._onPop) return;
        window.removeEventListener('popstate', this._onPop);
        this._onPop = null;
    }

    /**
     * 바텀시트를 닫는다.
     * @param fromPop 뒤로가기(popstate)로 들어온 호출인가. true 면 히스토리는 이미 되감긴
     *   상태라 back() 을 또 부르면 이전 페이지로 나가버린다.
     */
    /**
     * 헤더/본문 드래그로 닫기 (bottom sheet drag-to-dismiss).
     *
     * - 헤더에서는 항상 시작 가능. 본문(content-area)에서는 스크롤이 맨 위(scrollTop===0)일
     *   때만 시작 가능 — 그 아래로 스크롤된 상태면 일반 스크롤이 우선이라 드래그를 안 건다
     *   (콘텐츠 스크롤과 충돌 방지). 닫기/이전/다음 버튼 위에서 시작한 터치는 제외.
     * - 손을 뗀 시점에 "이동거리 ≥ DISMISS_PX" 이거나 "직전 구간 속도 ≥ VELOCITY_PX_MS" 이면
     *   close() — 짧고 빠르게 휙 내려서 손을 떼는 제스처(거리는 못 채워도 속도는 빠른 경우)도
     *   닫히게 한다. 둘 다 아니면 원위치로 스냅백.
     *   (2026-08 상필 님 QA — 거리 기준만 있을 때 빠른 스와이프가 안 먹히던 문제 + 본문에서
     *   드래그해도 반응이 없던 문제)
     * - 아래로만 따라 움직인다(위로 끌어도 패널이 위로 튀지 않게 0 으로 클램프).
     * - 패널은 CSS 에서 translate(-50%, …) 로 가로 중앙 정렬돼 있어, 드래그 중에도
     *   -50% X 를 유지한다(빠뜨리면 좌우로 튄다). 손을 떼면 인라인 transform 을 비워
     *   CSS(스냅백 또는 close 슬라이드다운)에 맡긴다.
     * - stopAnywhere(init 옵션) 를 켠 사이트는 손을 뗀 자리에 "멈춘다" — 닫히지 않을 만큼만
     *   내렸으면 그 오프셋(_dragRestY)을 유지하고, 다음 드래그는 그 자리(baseOffset)에서 이어
     *   간다. 위로 끝까지(맨 위 근처 ≤ SNAP_TOP_PX) 끌면 원위치(0)로 되돌리고, 아래로 임계값을
     *   넘기면 close(). 끄면(기본) 종전처럼 임계 미만은 전부 원위치 스냅백.
     * - Pointer Events 로 마우스/터치 공통 처리. 미지원 브라우저면 조용히 아무 것도 안 한다.
     */
    _bindDragToClose($el) {
        // 2026-08 상필 님 QA 영상 분석 — 실기기 제스처(헤더·본문 드래그 모두)가 45~90px 정도에서
        // 손을 떼는 경우가 많았는데, 기존 값(110px/0.6px·ms)엔 전부 못 미쳐 매번 스냅백됐다
        // (닫히지 않는 것처럼 보임). 다만 이 판정은 전 사이트 공용 로직이라 완화하면 장비불러·
        // 모바일관리자의 입력 폼 시트(차량번호·금액·전화번호·회원/오더 편집 등)에서 입력 중
        // 실수로 시트가 닫힐 위험이 있어 — init({looseDrag:true}) 로 켠 사이트(현재 악덕알리미만)만
        // 완화된 기준 + 본문 드래그를 쓰고, 나머지는 기존 동작(110px, 헤더 전용) 그대로.
        const self = this;
        const looseDrag = this._looseDrag;
        const DISMISS_PX = looseDrag ? 60 : 110;     // looseDrag: 60 (기존 110)
        const VELOCITY_PX_MS = 0.3;                  // looseDrag·stopAnywhere 에서 사용 — 이 속도(px/ms) 이상으로 아래로 튕기면 거리 미달이어도 닫힘
        const SNAP_TOP_PX = 8;                       // stopAnywhere: 이 값 이하로 올라오면 원위치(0)로 스냅
        const CLOSE_FRACTION = 0.4;                  // stopAnywhere: 패널 높이의 이 비율 이상 내려야 닫힘(그 미만은 그 자리에 멈춤)
        const $panel = $el.find('> .bottomsheet-panel');
        const $header = $panel.find('> .header');
        const $content = $panel.find('> .content-area');
        const panel = $panel[0];
        const content = $content[0];
        if (!panel || !window.PointerEvent) return;

        let startY = 0, baseOffset = 0, curOffset = 0, dragging = false, pid = null, fromContent = false;
        let lastY = 0, lastT = 0, velocity = 0;

        const onDown = (e) => {
            // 헤더의 버튼(닫기/이전/다음)에서 시작한 제스처는 클릭이 처리하게 둔다.
            // 띠(탭·검색)도 제외 — 탭을 옆으로 밀거나 검색칸을 누르다 시트가 끌려 내려가면 안 된다.
            if (e.target.closest('button, .bs-hd-band')) return;
            fromContent = $content.length > 0 && $.contains(content, e.target);
            // 본문은 맨 위에서 시작한 드래그만 허용 — 스크롤 중인 본문과 충돌 방지.
            if (fromContent && content.scrollTop > 0) return;
            dragging = true; pid = e.pointerId; startY = e.clientY;
            // stopAnywhere: 멈춰둔 자리에서 이어서 끈다. 기본(false)은 항상 전체열림(0)에서 시작.
            baseOffset = self._stopAnywhere ? self._dragRestY : 0;
            curOffset = baseOffset;
            lastY = e.clientY; lastT = e.timeStamp; velocity = 0;
            panel.style.transition = 'none';
            const $target = fromContent ? $content : $header;
            try { $target[0].setPointerCapture(pid); } catch (_) {}
        };
        const onMove = (e) => {
            if (!dragging) return;
            const raw = e.clientY - startY;         // 아래로 +, 위로 -
            // 전체열림(baseOffset 0) 상태의 본문에서 위로 튕기면 다시 스크롤에 맡긴다.
            // (멈춘 자리에서 위로 끄는 건 원위치로 되돌리는 동작이라 가로채야 한다)
            if (fromContent && baseOffset === 0 && raw < 0) return;
            curOffset = Math.max(0, baseOffset + raw);  // 위로는 원위치(0)까지만
            if (curOffset > 0) e.preventDefault();      // 본문 스크롤 이벤트로 새지 않게
            const dt = e.timeStamp - lastT;
            if (dt > 0) velocity = (e.clientY - lastY) / dt;
            lastY = e.clientY; lastT = e.timeStamp;
            panel.style.transform = `translate(-50%, ${curOffset}px)`;
        };
        const onUp = () => {
            if (!dragging) return;
            dragging = false;
            panel.style.transition = '';

            if (!self._stopAnywhere) {
                // 기존 동작: 임계거리(110/60) 또는 looseDrag 속도면 닫기, 아니면 원위치로 스냅백.
                const downDelta = curOffset - baseOffset;   // 이번 제스처 순(純) 내림
                panel.style.transform = '';
                if (downDelta >= DISMISS_PX || (looseDrag && downDelta > 0 && velocity >= VELOCITY_PX_MS)) this.close();
                return;
            }

            // stopAnywhere: 손 뗀 자리에 멈춘다.
            // 닫기는 '고정 px' 가 아니라 '패널 높이의 CLOSE_FRACTION 이상 내렸을 때'로 본다 —
            // 고정 px(110) 로 두면 짧은 시트에선 조금만 내려도 닫혀, "멈춤"이 안 먹던 문제(2026-10).
            // (전체 내림 오프셋 curOffset = 맨 위에서 지금까지 내린 총거리 기준)
            const panelH = panel.getBoundingClientRect().height || window.innerHeight || 0;
            const flicked = curOffset > baseOffset && velocity >= VELOCITY_PX_MS;  // 아래로 휙 튕김
            if (flicked || (panelH > 0 && curOffset >= panelH * CLOSE_FRACTION)) {
                self._dragRestY = 0;
                panel.style.transform = '';         // close 슬라이드다운에 맡김
                this.close();
            } else if (curOffset <= SNAP_TOP_PX) {
                self._dragRestY = 0;
                panel.style.transform = '';         // 맨 위 근처면 원위치로 되돌림(CSS transition)
            } else {
                self._dragRestY = curOffset;        // 멈춘 위치 유지(transition 복원돼도 값이 같아 안 움직임)
                panel.style.transform = `translate(-50%, ${curOffset}px)`;
            }
        };

        $header.on('pointerdown', onDown);
        $header.on('pointermove', onMove);
        $header.on('pointerup pointercancel', onUp);
        if (looseDrag && $content.length) {
            $content.on('pointerdown', onDown);
            $content.on('pointermove', onMove);
            $content.on('pointerup pointercancel', onUp);
        }
    }

    close(fromPop) {
        const self = this;

        // 히스토리 정리는 열림 여부와 무관하게 먼저 (닫는 중 중복 호출도 안전)
        this._offPop();
        const hadHist = this._histOn;
        this._histOn = false;

        const $el = $(`.${this.bsClsName}`);
        if (!$el.length || !$el.hasClass('is-open')) return;

        if (hadHist && !fromPop) history.back();   // 얹어둔 항목 회수

        const key = self.target ? $j.key(self.target) : null;
        $el.removeClass('is-open');

        // stopAnywhere 로 중간에 멈춰둔 인라인 transform 을 지운다 — 안 지우면 인라인이 CSS 를 이겨
        // 닫힘(translate 100%) 슬라이드다운이 안 돌고 뚝 사라진다. 지우면 멈춘 자리에서 슬라이드다운.
        const panelEl = $el.find('> .bottomsheet-panel')[0];
        if (panelEl) panelEl.style.transform = '';
        this._dragRestY = 0;

        // body unlock (애니메이션 동안에도 unlock 해도 시각적 문제 없음)
        this._unlockBody();

        // 애니메이션 끝나고 정리 / target.destroy / EventBus 알림
        this._closeTimer = setTimeout(() => {
            self._closeTimer = null;
            self.target?.destroy?.();
            self.target = null;
            self.destroy();
            $j.Common.EventBus.trigger('BS:close', { target: key });
        }, BS_DURATION);
    }

    /** 제목(HTML)만 바꾼다 — setHeader({ title }) 와 같다(로딩 막대를 잡아뒀으면 부제 자리도 걷힌다) */
    setTitle(title) {
        this.setHeader({ title });
    }

    /**
     * 모달이 돌려준 스냅샷을 원본 상태에 되쓴다. `Object.assign(ORDER, data)` 대신 쓴다.
     *
     * open({data}) 로 넘기는 것은 대개 얕은 복사(`Object.assign({}, ORDER, {onApply})`)이고,
     * 모달은 apply 때 `cb(this.data)` 로 그 스냅샷을 **통째로** 돌려준다 (모달 8개가 전부 그렇다).
     * 그대로 되쓰면 모달이 소유하지도 않은 것까지 '열었을 때 값' 으로 되돌아간다.
     * 되돌아가면 안 되는 두 가지를 여기서 뺀다 —
     *
     *   $r      모달→화면 채널. 참조를 갈아끼우는 모달이 하나만 있어도 앞선 모달이 넣어둔 값이
     *           통째로 날아간다. 실제로 작업일 모달의 `this.data.$r={}` 때문에 지정 선택
     *           (ORDER.$r.order_type)이 사라져 '지정 대상이 설정되지 않았습니다' 가 떴다.
     *   onApply 콜백 함수. 상태에 남으면 그 상태를 통째로 전송하는 순간 jQuery $.param 이
     *           함수 값을 만나 value() 로 **호출해버린다**.
     *
     * 비동기로 채워지는 필드(가격·저단가 하한 등)는 화면마다 달라 호출부가 직접 지운다 —
     * 발주화면(F/Baljoo/Baljoo.js onModalApply)이 그 예다.
     */
    applySnapshot(state, data) {
        if (!state || !data) return;
        delete data['$r'];
        delete data['onApply'];
        Object.assign(state, data);
        delete state['onApply'];// 예전에 무방비로 되쓴 흔적까지 걷어낸다
    }

    // ── body scroll lock (iOS 안전 패턴) ──
    _lockBody() {
        // 이미 잠겨있다면 (다른 BS 가 열린 상태) 중첩 잠그지 않음
        if (document.body.classList.contains('bs-body-locked')) return;
        this._savedScrollY = window.scrollY || window.pageYOffset || 0;
        document.body.style.position = 'fixed';
        document.body.style.top = `-${this._savedScrollY}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.width = '100%';
        document.body.classList.add('bs-body-locked');
    }

    _unlockBody() {
        if (!document.body.classList.contains('bs-body-locked')) return;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.width = '';
        document.body.classList.remove('bs-body-locked');
        // 잠금 전 스크롤 위치 복원
        window.scrollTo(0, this._savedScrollY);
    }

    /**
     * BS 진단 로깅 — URL 쿼리스트링 ?bsDebug=1 또는 window.BS_DEBUG=true 로 활성화.
     * 각 layer 의 rect / overflow / scrollH·clientH 를 출력 + 추가 스크롤 컨테이너 자동 탐지.
     */
    _debug(label) {
        const on = (typeof window !== 'undefined') &&
                   (window.BS_DEBUG === true || /[?&]bsDebug=1\b/.test(location.search));
        if (!on) return;

        const $el = $(`.${this.bsClsName}`);
        if (!$el.length) { console.log(`[BS:${label}] (no .${this.bsClsName} in DOM)`); return; }

        const layers = [
            ['popup',         $el[0]],
            ['overlay',       $el.find('> .bottomsheet-overlay')[0]],
            ['panel',         $el.find('> .bottomsheet-panel')[0]],
            ['header',        $el.find('> .bottomsheet-panel > .header')[0]],
            ['content-area',  $el.find('> .bottomsheet-panel > .content-area')[0]],
        ];

        const inner = $el.find('*').toArray();
        const layerEls = new Set(layers.map(l => l[1]).filter(Boolean));
        const scrollContainers = inner.filter(el => {
            const cs = getComputedStyle(el);
            return /(auto|scroll)/.test(cs.overflowY + ' ' + cs.overflowX);
        }).filter(el => !layerEls.has(el));

        const blocked = inner.filter(el => {
            const cs = getComputedStyle(el);
            return cs.touchAction === 'none' || cs.touchAction === 'pan-x' || cs.touchAction === 'pan-y';
        }).slice(0, 10);

        console.groupCollapsed(`[BS:${label}] viewport=${innerWidth}x${innerHeight} fixedHeight=${this.fixedHeight}`);
        for (const [name, el] of layers) {
            if (!el) { console.log(`  ${name}: (없음)`); continue; }
            const cs = getComputedStyle(el);
            const r = el.getBoundingClientRect();
            const sH = el.scrollHeight, cH = el.clientHeight;
            const scrollable = sH > cH && /(auto|scroll)/.test(cs.overflowY);
            const inline = el.getAttribute('style') || '';
            const cls = (el.className || '').toString().split(/\s+/).filter(Boolean).join(' ');
            console.log(
                `  ${name}\n` +
                `    rect: ${Math.round(r.top)},${Math.round(r.left)} ${Math.round(r.width)}x${Math.round(r.height)}\n` +
                `    scroll: scrollH=${sH} clientH=${cH} ${scrollable ? '✓ scroll OK' : (sH > cH ? '✗ overflow막힘' : '스크롤불필요')}\n` +
                `    overflow: ${cs.overflow} (x=${cs.overflowX} y=${cs.overflowY})  touch-action: ${cs.touchAction}\n` +
                `    position: ${cs.position}  transform: ${cs.transform === 'none' ? '-' : cs.transform}\n` +
                (inline ? `    inline: ${inline}\n` : '') +
                `    class: ${cls}`,
                el
            );
        }

        if (scrollContainers.length) {
            console.log(`  ── BS 내부 추가 스크롤 컨테이너 ${scrollContainers.length}개 ──`);
            scrollContainers.forEach(el => {
                const cs = getComputedStyle(el);
                const r = el.getBoundingClientRect();
                const sH = el.scrollHeight, cH = el.clientHeight;
                const scrollable = sH > cH && /(auto|scroll)/.test(cs.overflowY);
                const cls = (el.className || '').toString().split(/\s+/).filter(Boolean).slice(0, 4).join('.');
                const id = el.id ? '#' + el.id : '';
                console.log(
                    `    <${el.tagName.toLowerCase()}${id}.${cls}>\n` +
                    `      rect: ${Math.round(r.width)}x${Math.round(r.height)} @${Math.round(r.top)},${Math.round(r.left)}\n` +
                    `      scroll: scrollH=${sH} clientH=${cH} ${scrollable ? '✓ scroll OK' : (sH > cH ? '✗ overflow막힘' : '스크롤불필요')}\n` +
                    `      overflow: ${cs.overflow} (x=${cs.overflowX} y=${cs.overflowY})  touch-action: ${cs.touchAction}`,
                    el
                );
            });
        } else {
            console.log(`  ── BS 내부에 추가 스크롤 컨테이너 없음`);
        }

        if (blocked.length) {
            console.warn(`  touch-action 차단 element ${blocked.length}개:`);
            blocked.forEach(el => console.warn('    ', getComputedStyle(el).touchAction, el));
        }
        console.log(`  body locked: ${document.body.classList.contains('bs-body-locked')}`);
        console.groupEnd();
    }
}
